import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';
import { toastWarning } from '@components/Toast';
import CustomTooltip from '@components/CustomTooltip';

import {
	useDisableClientByIdMutation,
	useLazyFetchClientsByBrandQuery,
	useUpdateClientByIdMutation
} from '@features/crm/brands/api/brandProfileApi';
import ClientWiseDepartment from '@features/crm/brands/components/nitex-operation-team/ClientWiseDepartment';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { capitalizeFirstLetter, getPrimaryUserType } from '@utils/Util';

const TAB_LIST = [
	{ title: 'Active team', value: 'ACTIVE' },
	{ title: 'Disable team', value: 'DISABLED' }
];

const BrandClientList = ({ users }) => {
	const navigate = useNavigate();
	const [status, setStatus] = useState('ACTIVE');
	const [fetchClients, { data, isLoading }] = useLazyFetchClientsByBrandQuery();
	const [updateClientById] = useUpdateClientByIdMutation();
	const [disableClientById] = useDisableClientByIdMutation();
	const [selectedMember, setSelectedMember] = useState({});
	const [selectedClient, setSelectedClient] = useState(null);
	const dispatch = useDispatch();
	const { id } = useParams();

	useEffect(() => {
		fetchClients(`?brandId=${id}&status=${status}&page=0&size=100&sort=id,desc`);
	}, [status]);

	const renderDropdownOptions = (userTypes = []) => {
		let tmpUsers = [];
		for (let type of userTypes) {
			let selectedUsers = users?.[type] || [];
			tmpUsers = [...tmpUsers, ...selectedUsers];
		}

		return (
			tmpUsers.map((user) => ({
				label: user.name,
				value: user.id
			})) || []
		);
	};

	const handleSelectChange = (option, brand, type) => {
		setSelectedMember((prev) => ({ ...prev, [type]: option }));
	};

	const handleBlur = async (type) => {
		dispatch(openLoader());
		let requestBody = {};
		if (type === 'BDM') {
			requestBody['bdmId'] = selectedMember?.bdm?.value || null;
		}
		if (type === 'SECONDARY_ACCOUNT_MANAGERS') {
			requestBody['accountManagerIdList'] =
				selectedMember?.secondaryAccountManagers?.map((item) => item.value) || [];
		}
		if (type === 'FASHION_DESIGNERS') {
			requestBody['designerIdList'] = selectedMember?.fashionDesigners?.map((item) => item.value) || [];
		}
		if (type === 'ACCOUNT_MANAGER') {
			requestBody['executiveId'] = selectedMember?.primaryAccountManager?.value || null;
		}

		await updateClientById({
			clientId: selectedMember.id,
			requestBody: requestBody
		});
		dispatch(closeLoader());
	};

	const renderMarketCategories = (brand) => {
		const firstResponse = brand?.marketCategoryResponses?.[0];
		if (firstResponse) {
			return (
				<p>
					{firstResponse?.productGroupId ? `${firstResponse.productGroupName}-` : ''}
					{firstResponse?.fabricType ? `${capitalizeFirstLetter(firstResponse.fabricType)}-` : ''}
					{firstResponse?.categoryList?.length > 0
						? `${firstResponse.categoryList?.map((item) => item.name).join(',')}`
						: ''}
				</p>
			);
		}
	};

	const renderMarketCategoriesToolTipResponse = (brand) => {
		let txt = '';
		brand?.marketCategoryResponses?.forEach((firstResponse) => {
			let line = '';
			line += firstResponse?.productGroupId ? `${firstResponse.productGroupName}-` : '';
			line += firstResponse?.fabricType ? `${capitalizeFirstLetter(firstResponse.fabricType)}-` : '';
			line +=
				firstResponse?.categoryList?.length > 0
					? `${firstResponse.categoryList?.map((item) => item.name).join(',')} | `
					: '';
			txt += line;
		});
		return txt.trim().slice(0, -1);
	};

	const noBorderStyles = {
		control: (base) => ({
			...base,
			borderColor: 'transparent',
			boxShadow: 'none',
			'&:hover': {
				borderColor: '#e4e7eb',
				backgroundColor: '#fff'
			}
		})
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="active-buyer-and-assigned-members mt-3">
				<div className="title-and-action d-flex align-items-center justify-content-between mb-3">
					<div className="title">
						<div className="inner-tabs mb-0">
							<ul>
								{TAB_LIST?.map((item) => {
									return (
										<li
											className={status === item.value ? 'active' : ''}
											key={item.value}
											onClick={() => setStatus(item.value)}
										>
											{item.title}
										</li>
									);
								})}
							</ul>
						</div>
					</div>
					<div className="action-buyer d-flex align-items-center">
						<div className="add-new-design pl-2">
							<button
								className="button text size32 px-0"
								onClick={() => window.open('/clients/add', '_blank')}
							>
								<CommonImage src="/icons/add-round.svg" alt="add" className="ml-1" />
								<span className="ml-1">Buyer</span>
							</button>
						</div>
					</div>
				</div>
				<p className="regular-14 gray_dark_01 pb-3">
					To assign any member in this table, please add that member in the team section above
					first.
				</p>
				<table className="table height-sm with-border hover scroll-x-label">
					<thead>
						<tr>
							<th>Buyer name & Email</th>
							<th>Buyer Department</th>
							<th>BDM</th>
							<th>Senior AM</th>
							<th>Others AM</th>
							<th>Designer</th>
							<th>Action</th>
						</tr>
					</thead>
					<tbody>
						{data?.data?.map((brand) => {
							const bdmList = renderDropdownOptions(['BUSINESS_DEVELOPMENT_MANAGER']);
							const accountManagerList = renderDropdownOptions(['ACCOUNT_MANAGER']);
							const fashionDesignerList = renderDropdownOptions(['FASHION_DESIGNER']);
							const toolTipText = renderMarketCategoriesToolTipResponse(brand);
							return (
								<tr
									onClick={() => {
										if (selectedMember?.id !== brand.id) {
											setSelectedMember(brand);
										}
									}}
									key={`brand-${brand.id}`}
									className={selectedMember?.id === brand.id ? 'active' : ''}
								>
									<td className="buyer-column">
										<p className="gray_dark_01">{brand?.name}</p>
										<p className="gray_dark_01">{brand?.email}</p>
										<p>{capitalizeFirstLetter(brand?.brandDepartment)}</p>
									</td>
									<td
										className="buyer-department"
										onClick={() => {
											if (selectedMember?.id === brand.id) {
												setSelectedClient(brand.id);
											}
										}}
									>
										<CustomTooltip
											placement="top"
											arrowCenter={true}
											text={toolTipText}
											className={toolTipText?.length > 0 ? 'show' : 'hide'}
										>
											<div className="d-flex align-items-center cursor-pointer">
												<p className="gray_dark_01">Add</p>
												<CommonImage src="/icons/down-arrow-gray-dark-01.svg" />
											</div>

											{renderMarketCategories(brand)}
										</CustomTooltip>
									</td>
									<td className="bdm-column">
										<div className="form-group no-shadow-react-select mb-0">
											<Select
												options={bdmList}
												menuPlacement="top"
												value={
													selectedMember?.id !== brand.id
														? brand?.bdm
														: selectedMember?.bdm
												}
												onMenuOpen={() => {
													if (bdmList.length === 0) {
														toastWarning(
															'No BDM available. Please add BDM in the team section first.'
														);
													}
												}}
												onChange={(option) =>
													handleSelectChange(option, brand, 'bdm')
												}
												isDisabled={selectedMember?.id !== brand.id}
												onBlur={async () => {
													if (bdmList.length > 0) {
														await handleBlur('BDM');
													}
												}}
												styles={noBorderStyles}
											/>
										</div>
									</td>
									<td className="senior-column">
										<div className="form-group no-shadow-react-select mb-0">
											<Select
												options={accountManagerList}
												value={
													selectedMember?.id !== brand.id
														? brand?.primaryAccountManager
														: selectedMember?.primaryAccountManager
												}
												menuPlacement="top"
												onMenuOpen={() => {
													if (accountManagerList.length === 0) {
														toastWarning(
															'No Primary Account Manager available. Please add Primary Account Manager in the team section first.'
														);
													}
												}}
												onChange={(option) =>
													handleSelectChange(option, brand, 'primaryAccountManager')
												}
												isDisabled={selectedMember?.id !== brand.id}
												onBlur={async () => {
													if (accountManagerList.length > 0) {
														await handleBlur('ACCOUNT_MANAGER');
													}
												}}
												styles={noBorderStyles}
											/>
										</div>
									</td>
									<td className="other-column">
										<div className="form-group no-shadow-react-select mb-0">
											<Select
												options={accountManagerList}
												isMulti
												menuPlacement="top"
												value={
													selectedMember?.id !== brand.id
														? brand?.secondaryAccountManagers
														: selectedMember?.secondaryAccountManagers
												}
												onChange={(option) =>
													handleSelectChange(
														option,
														brand,
														'secondaryAccountManagers'
													)
												}
												onMenuOpen={() => {
													if (accountManagerList.length === 0) {
														toastWarning(
															'No Account Manager available. Please add Account Manager in the team section first.'
														);
													}
												}}
												isDisabled={selectedMember?.id !== brand.id}
												onBlur={async () => {
													if (accountManagerList.length > 0) {
														await handleBlur('SECONDARY_ACCOUNT_MANAGERS');
													}
												}}
												styles={noBorderStyles}
											/>
										</div>
									</td>
									<td className="designer-column">
										<div className="form-group no-shadow-react-select mb-0">
											<Select
												options={fashionDesignerList}
												isMulti
												menuPlacement="top"
												value={
													selectedMember?.id !== brand.id
														? brand?.fashionDesigners
														: selectedMember?.fashionDesigners
												}
												onChange={(option) =>
													handleSelectChange(option, brand, 'fashionDesigners')
												}
												onMenuOpen={() => {
													if (fashionDesignerList.length === 0) {
														toastWarning(
															'No Fashion Designer available. Please add Fashion Designer in the team section first.'
														);
													}
												}}
												isDisabled={selectedMember?.id !== brand.id}
												onBlur={async () => {
													if (fashionDesignerList.length > 0) {
														await handleBlur('FASHION_DESIGNERS');
													}
												}}
												styles={noBorderStyles}
											/>
										</div>
									</td>
									<td
										className={`action-column ${!['ADMIN', 'ACCOUNT_MANAGER', 'BUSINESS_DEVELOPMENT_MANAGER'].includes(getPrimaryUserType()) ? 'cursor-none' : ''}`}
									>
										<div className="option">
											<div className="dropdown">
												<button
													className="btn btn-default dropdown-toggle"
													type="button"
													id="menu1"
													data-toggle="dropdown"
													aria-expanded="false"
												>
													<CommonImage src="/icons/more_icon.svg" alt="" />
												</button>
												<ul
													className="dropdown-menu dropdown-menu-right shadow-lg"
													role="menu"
													aria-labelledby="menu1"
													x-placement="bottom-end"
												>
													{/*<li*/}
													{/*	role="presentation"*/}
													{/*	className="px-4 pb-3 pt-3"*/}
													{/*	onClick={() => {*/}
													{/*		setSelectedMember(brand);*/}
													{/*	}}*/}
													{/*>*/}
													{/*	<a*/}
													{/*		role="menuitem"*/}
													{/*		tabIndex={-1}*/}
													{/*		className="font-weight-normal  text-black"*/}
													{/*	>*/}
													{/*		Edit team*/}
													{/*	</a>*/}
													{/*</li>*/}
													<li
														role="presentation"
														className="px-4 pb-3"
														onClick={() => navigate(`/clients/edit/${brand.id}`)}
													>
														<a
															role="menuitem"
															tabIndex={-1}
															className="font-weight-normal  text-black"
															href
														>
															Edit client details
														</a>
													</li>
													<li
														role="presentation"
														className="px-4 pb-3"
														onClick={() => {
															disableClientById({
																clientId: brand.id,
																status:
																	status === 'ACTIVE'
																		? 'DISABLED'
																		: 'ACTIVE'
															});
														}}
													>
														<a
															role="menuitem"
															tabIndex={-1}
															className="font-weight-normal  text-black"
															href
														>
															{status === 'ACTIVE' ? 'Disable' : 'Enable'} buyer
														</a>
													</li>
												</ul>
											</div>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{selectedClient !== null && (
				<ClientWiseDepartment
					onClose={() => {
						setSelectedMember({});
						setSelectedClient(null);
					}}
					clientId={selectedClient}
					selected={selectedMember?.marketCategoryResponses || []}
				/>
			)}
		</LoaderComponent>
	);
};

export default BrandClientList;
